import { useEffect } from 'react';
import _get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import {
  Department,
  FilterGroupingEnum,
  useCountriesQuery,
  User,
  UserCreateInputObject,
  UserUpdateInputObject,
  useDepartmentsSearchListLazyQuery,
  useDepartmentsSearchListQuery,
  useSectorsSearchListLazyQuery,
  useSitesSearchListLazyQuery,
  useTeamsSearchListLazyQuery,
  useUserCreateMutation,
  useUserQuery,
  useUserUpdateMutation,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import getPreparedDataFromValues from 'routes/Users/utils/getPreparedDataFromValues';
import getAppUrlWithDomain from '@lib/utils/getAppUrlWithDomain';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage, getGraphQLErrors } from '@lib/utils/graphQLErrors';
import { APP_URLS } from 'constants/urls';
import { getSitesLoadOptions as getSitesLoadOptionsFn } from 'utils/fetch/searchLists/sitesSearchList';
import { getSectorsLoadOptions as getSectorsLoadOptionsFn } from 'utils/fetch/searchLists/sectorsSearchList';
import getDefaultValuesFromQueryData from 'routes/Users/utils/getDefaultValuesFromQueryData';
import { getTeamsLoadOptions as getTeamsLoadOptionsFn } from 'utils/fetch/searchLists/teamsSearchList';
import { getDepartmentsLoadOptions as getDepartmentsLoadOptionsFn } from 'utils/fetch/searchLists/departmentsSearchList';

const { VITE_AUTH_PLATFORM_URL: AUTH_PLATFORM } = import.meta.env;

interface Props {
  onSubmitCallback: (v?: User | null) => void;
  isOpen: boolean;
  selectedUserId?: string | null;
}

export default function useActions(props: Props) {
  const { selectedUserId, isOpen, onSubmitCallback } = props;
  const { t } = useTranslation();

  // User query
  const userQuery = useUserQuery({
    skip: !selectedUserId,
    fetchPolicy: 'cache-and-network',
    variables: {
      id: selectedUserId || '',
      includeFullData: true,
    },
  });
  const selectedUser = _get(userQuery, 'data.data') as User;
  const userLoading = _get(userQuery, 'loading');
  const userError = _get(userQuery, 'error');

  // Countries query
  const countriesQuery = useCountriesQuery({
    fetchPolicy: 'cache-first',
  });
  const countries = _get(countriesQuery, 'data.data', []);
  const countriesCodes = countries.map(({ code }) => code.toLowerCase());
  const countriesError = _get(countriesQuery, 'error');

  // Sites query
  const [fetchSitesSearchListLazyQuery] = useSitesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const sitesLoadOptions = getSitesLoadOptionsFn(
    fetchSitesSearchListLazyQuery,
    undefined,
    { includeSector: true },
  );

  // Sectors query
  const [fetchSectorsSearchListLazyQuery] = useSectorsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const sectorsLoadOptions = getSectorsLoadOptionsFn(
    fetchSectorsSearchListLazyQuery,
  );

  // Teams query
  const [fetchTeamsSearchListLazyQuery] = useTeamsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const teamsLoadOptions = getTeamsLoadOptionsFn(fetchTeamsSearchListLazyQuery);

  // Departments query
  const [fetchDepartmentsSearchListLazyQuery] =
    useDepartmentsSearchListLazyQuery({
      fetchPolicy: 'cache-and-network',
    });

  // Field never opens its own menu (see DepartmentsModalTableMultiselect
  // usage below); the wrapped fn only needs to resolve labels for already-
  // selected values, matching Task/Intervention/Team's `getLoadOptions`
  // usage for the same field pattern.
  const getDepartmentsLoadOptions = () =>
    getDepartmentsLoadOptionsFn(fetchDepartmentsSearchListLazyQuery);

  // Lightweight fetch to prefill the "Departments" select on edit — Department
  // has no reverse field on User, only a `usersId` filter on the Department
  // side (mirrors Team's `linkedDepartments` fetch).
  const linkedDepartmentsQuery = useDepartmentsSearchListQuery({
    fetchPolicy: 'cache-and-network',
    skip: !selectedUserId,
    variables: {
      limit: 100,
      filters: [
        {
          usersId: {
            grouping: FilterGroupingEnum.And,
            predicate: { eq: selectedUserId || '' },
          },
        },
      ],
    },
  });
  const linkedDepartments = _get(
    linkedDepartmentsQuery.data || linkedDepartmentsQuery.previousData,
    'data.collection',
    [],
  ) as Department[];

  // Mutations
  const [createMutation, createMutationResult] = useUserCreateMutation({
    onCompleted: (response) => {
      toast({ content: t('create-team-member-success') });
      onSubmitCallback(response?.data as User);
    },
    onError: (error) => {
      toast({ content: getErrorMessage(error, t) });
    },
  });

  const [updateMutation, updateMutationResult] = useUserUpdateMutation({
    // `departmentMemberships` isn't part of the User mutation response, so
    // any already-mounted `departmentsSearchList` query (the view page's
    // "Department" row, this form's own `linkedDepartments` prefill) is
    // stale after a user edit — refetch by operation name to pick up the
    // change instead of leaving them showing pre-edit data.
    refetchQueries: ['departmentsSearchList'],
    onCompleted: (response) => {
      toast({ content: t('update-team-member-success') });
      onSubmitCallback(response?.data as User);
    },
    onError: (error) => {
      toast({ content: getErrorMessage(error, t) });
    },
  });

  const onCreateHandler = (values: UserCreateInputObject) => {
    createMutation({
      variables: {
        attributes: values,
        redirectUrl: getAppUrlWithDomain(
          APP_URLS.setup.finishRegistration.path,
          AUTH_PLATFORM,
        ),
      },
    });
  };

  const onUpdateHandler = (values: UserUpdateInputObject) => {
    if (selectedUserId) {
      updateMutation({
        variables: {
          id: selectedUserId,
          attributes: values,
          finishRegistrationRedirectUrl: getAppUrlWithDomain(
            APP_URLS.setup.finishRegistration.path,
            AUTH_PLATFORM,
          ),
          confirmEmailRedirectUrl: getAppUrlWithDomain(
            APP_URLS.setup.confirmEmail.path,
            AUTH_PLATFORM,
          ),
        },
      });
    }
  };

  const onSubmitUserForm = (values: Values) => {
    const prevData = {
      ...selectedUser,
    };
    const prevValues = getDefaultValuesFromQueryData(
      t,
      prevData,
      linkedDepartments,
    );
    const newValues = getPreparedDataFromValues(values, prevValues);
    if (selectedUserId) {
      onUpdateHandler(newValues as UserUpdateInputObject);
    } else {
      onCreateHandler(newValues as UserCreateInputObject);
    }
  };

  const {
    error: createMutationError,
    loading: createUserLoading,
    reset: createUserReset,
  } = createMutationResult;
  const {
    error: updateMutationError,
    loading: updateUserLoading,
    reset: updateUserReset,
  } = updateMutationResult;
  const graphQLErrors = selectedUserId
    ? getGraphQLErrors(updateMutationError)
    : getGraphQLErrors(createMutationError);
  const loading = userLoading || createUserLoading || updateUserLoading;

  useEffect(() => {
    if (!isOpen) {
      createUserReset();
      updateUserReset();
    }
  }, [createUserReset, isOpen, updateUserReset]);

  return {
    countriesCodes,
    countriesError,
    getDepartmentsLoadOptions,
    graphQLErrors,
    linkedDepartments,
    loading,
    onSubmitUserForm,
    sectorsLoadOptions,
    selectedUser,
    sitesLoadOptions,
    teamsLoadOptions,
    userError,
    userLoading,
  };
}
